import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Loader2 } from "lucide-react";
import makLogo from "@/assets/mak-logo.png";
import makLogoWhite from "@/assets/mak-logo-white.png";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Došlo je do greške. Pokušajte ponovo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between bg-primary text-primary-foreground p-12">
        <img src={makLogoWhite} alt="MAK" className="w-44 h-auto self-start" />
        <div className="space-y-4 max-w-md">
          <h1 className="font-heading text-4xl font-extrabold leading-tight">Vaše poslovanje na jednom mjestu.</h1>
          <p className="text-primary-foreground/70 text-lg leading-relaxed">
            Kupci i poslovi, šihtarica radnika, rokovi, mjesečni troškovi i fakture dobavljača — jednostavnije od Excela.
          </p>
        </div>
        <p className="text-primary-foreground/50 text-sm">© 2026 MAK · KM · BAM</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6">
          <div className="lg:hidden mb-2">
            <img src={makLogo} alt="MAK" className="h-10 w-auto" />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-bold tracking-tight">Prijava</h2>
            <p className="text-sm text-muted-foreground mt-1">Prijavite se na svoj račun</p>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vas@email.com" required data-testid="login-email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Lozinka</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required data-testid="login-password" />
            </div>
          </div>

          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="login-error">{error}</div>}

          <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Prijavi se
          </Button>
        </form>
      </div>
    </div>
  );
}
